import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Base64 } from 'js-base64'
import * as Yup from 'yup'
import { Formik, FormikHelpers, Form } from 'formik'
import {
  FiUser,
  FiSmartphone,
  FiMail,
  FiSend,
  FiCalendar,
  FiCheckCircle,
} from 'react-icons/fi'
import usePersistedState from '../../hooks/usePersistedState'
import api from '../../services/api'
import Button from '../../components/Button'
import Input from '../../components/Input'
import Header from '../../components/Header'
import { UserData, ConfigData, ErroProps } from '../../utils/interfaces'
import ModalBox from '../../components/Modal'
import { RadioGroup } from '../../components/RadioGroup/radio-group'
import validaCPF from '../../utils/validaCPF'
import calculaIdade from '../../utils/calculaIdade'
import moment from 'moment'
import { MdSecurity } from 'react-icons/md'
import { Cpf } from '../../utils/masks'
import CircularProgress from '@mui/material/CircularProgress'

type RegisterFormValues = {
  name: string
  birthdate: string
  cpf: string
  phone: string
  email: string
  tipContato: string
}

const Register: React.FC = () => {
  const [userData, setUserData] = usePersistedState<UserData>(
    'userData',
    {} as UserData,
  )
  const [configData, setConfigData] = usePersistedState<ConfigData>(
    'configData',
    {} as ConfigData,
  )
  const [, setErroProps] = usePersistedState<ErroProps>(
    'erroProps',
    {} as ErroProps,
  )

  const [tipoContato, setTipoContato] = usePersistedState('tipoContato', 'S')
  const [clicado, setClicado] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [warningText, setWarningText] = useState('')

  const navigate = useNavigate()

  function handleCloseModal(): void {
    setIsModalOpen(false)
  }
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string()
          .required('Seu nome é obrigatório.')
          .matches(/\s/g, 'Digite o nome completo')
          .min(5, 'Digite o nome completo'),
        tipContato: Yup.string().required(),
        email: Yup.string()
          .required('E-mail pessoal é obrigatório')
          .email('Digite um e-mail vá­lido'),
        phone: Yup.string()
          .max(15, 'Telefone invá­lido')
          .required('Celular é obrigatório'),
        birthdate: Yup.string()
          .test(
            '',
            'A data de nascimento não pode ser maior que hoje.',
            (value) =>
              moment() > moment((value ?? '').split('/').reverse().join('-')) ||
              value === '',
          )
          .test(
            '',
            'Data de nascimento invá­lida',
            (value) =>
              moment((value ?? '').split('/').reverse().join('-')).isValid() ||
              value === '',
          )
          .test(
            '',
            'Data de nascimento invá­lida',
            (value) =>
              calculaIdade((value ?? '').split('/').reverse().join('-')) <=
                115 || value === '',
          )
          .required('Data de nascimento obrigatória'),
        cpf: Yup.string()
          .required('CPF obrigatório.')
          .test('', 'CPF inválido', (value) => {
            const cpf = value ?? ''
            return (
              validaCPF(cpf.replace(/\./g, '').replace('-', '')) || cpf === ''
            )
          }),
      }),
    [],
  )
  const initialValues: RegisterFormValues = useMemo(
    () => ({
      name: userData?.name ?? '',
      birthdate:
        (userData?.birthdate || '').split('-').reverse().join('/') ?? '',
      cpf: Cpf(userData?.cpf || '') ?? '',
      phone: userData?.phone ?? '',
      email: userData?.email ?? '',
      tipContato: tipoContato,
    }),
    [userData, tipoContato],
  )

  const handleSubmit = useCallback(
    async (
      values: RegisterFormValues,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _helpers: FormikHelpers<RegisterFormValues>,
    ) => {
      try {
        setClicado(true)

        await setUserData({
          ...userData,
          name: values.name,
          birthdate: values.birthdate?.split('/').reverse().join('-'),
          cpf: values.cpf?.replace(/\./g, '').replace(/-/g, ''),
          phone: values.phone,
          email: values.email,
        })

        setTipoContato(values.tipContato)

        const parametros = Base64.encode(
          `{"versao":"${configData.tipo}",
          "plano": "${configData.plano}",
          "cliente":"${configData.codCliente}",
          "cpf":"${values.cpf?.replace(/\./g, '').replace(/-/g, '')}",
          "email":"${values.email === undefined ? '' : values.email}",
          "telefone":"${
            values.phone === undefined
              ? ''
              : `55${values.phone
                  .replace('(', '')
                  .replace(') ', '')
                  .replace('-', '')}`
          }",
          "nascimento":"${values.birthdate?.split('/').reverse().join('-')}",
          "envio":"${values.tipContato}"}`,
        )

        const parametrosFinal = Base64.encode(parametros)
        // navigate('/register/confirm-sms')
        await api
          .get(`wsAutenticacaoIprejun.rule?sys=ADZ&Entrada=${parametrosFinal}`)
          .then((res) => {
            setConfigData({ ...configData, token: res.data.token, tipoContato })
            navigate('/register/confirm-sms')
          })
          .catch((res) => {
            if (res.message === 'Network Error') {
              setErroProps({
                title: res.message,
                description: 'Falha na conexão como servidor',
              })
              setClicado(false)
              navigate('/erro')
            } else if (res.message === 'Request failed with status code 500') {
              setErroProps({
                title: 'Erro interno no servidor',
                description: res.message,
              })
              setClicado(false)
              navigate('/erro')
            } else if (
              res.message === 'Request failed with status code 404' ||
              res.message === 'Request failed with status code 403'
            ) {
              setIsModalOpen(true)
              setWarningText(
                'Não localizamos os seus dados na base cadastral. Por favor, confira as informações digitadas ou entre em contato com a á­rea de atendimento.',
              )
              setClicado(false)
            } else if (res.message === 'Request failed with status code 401') {
              setIsModalOpen(true)
              setWarningText('Você já­ é participante.')
              setClicado(false)
            } else {
              setIsModalOpen(true)
              setWarningText(
                'Ops, algo deu errado. Tente novamente mais tarde.',
              )
              setClicado(false)
            }
          })
      } catch {
        setClicado(false)
        setIsModalOpen(true)
        setWarningText('Ops, algo deu errado. Tente novamente mais tarde.')
      }
    },
    [
      setUserData,
      userData,
      configData,
      tipoContato,
      setConfigData,
      setErroProps,
      navigate,
    ],
  )

  return (
    <>
      <Header />
      <div className="mx-auto mb-[15px] flex max-w-[600px] flex-col items-center px-[5px] max-[766px]:max-w-[550px]">
        <div className="w-full rounded-[8px] bg-white p-[30px_25px] shadow-[0px_2px_4px_rgba(0,0,0,0.05),0px_6px_12px_rgba(61,69,67,0.05)] mb-[10px] mx-[5px] max-[766px]:p-[20px_15px]">
          <strong className="mb-[15px] flex justify-center text-center text-[18px] text-[#0071ba]">
            Informe seus dados para prosseguir:
          </strong>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ submitForm }) => (
              <Form className="flex flex-col items-center">
                <Input name="name" placeholder="Nome Completo" icon={FiUser} />
                <Input
                  placeholder="CPF"
                  name="cpf"
                  icon={MdSecurity}
                  type="tel"
                  mask="cpf"
                />
                <Input
                  icon={FiCalendar}
                  name="birthdate"
                  mask="date"
                  placeholder="Data de nascimento"
                />
                <Input
                  name="phone"
                  mask="phone"
                  prefix="+55 | "
                  placeholder="Celular com DDD"
                  icon={FiSmartphone}
                />
                <Input
                  icon={FiMail}
                  name="email"
                  id="email"
                  type="email"
                  placeholder="E-mail"
                  maxLength={150}
                />
                <RadioGroup
                  name="tipContato"
                  icon={FiSend}
                  value={tipoContato}
                  placeholder="Enviar código por:?"
                  options={[
                    { label: 'SMS', value: 'S' },
                    { label: 'E-mail', value: 'E' },
                  ]}
                />

                <Button
                  type="button"
                  color="blue"
                  onClick={submitForm}
                  disabled={clicado}
                  className="mt-8"
                >
                  {!clicado ? (
                    <>
                      Confirmar
                      <FiCheckCircle size={24} />
                    </>
                  ) : (
                    <>
                      <CircularProgress color="inherit" size={24} /> Aguarde
                    </>
                  )}
                </Button>
              </Form>
            )}
          </Formik>
        </div>

        <ModalBox isOpen={isModalOpen} onRequestClose={handleCloseModal}>
          <p>{warningText}</p>
        </ModalBox>
      </div>
    </>
  )
}

export default Register
